import type { ApiKeyAttributes as GQLInput, ApiKeyPermissionAttributes } from '../../generated/api.graphql';
import { take } from '../../util';

/**
 * The permission set, aliased rather than restated: it is a fixed 50-key input whose keys the
 * mutation rejects outright if one is wrong, so an inner whitelist would guard against nothing.
 */
export type PermissionAttributes = ApiKeyPermissionAttributes;

/**
 * What a caller hands `toGqlInput` to create a key. `accountId` and `permission` are the two the
 * mutation requires — a key is always scoped. `expiresAt` is absent rather than computed for a
 * one-year lifetime: the server defaults to a year and caps there, so a client-computed value sits
 * exactly on the cap.
 */
export type ApiKeyInput = {
  accountId: number;
  permission: PermissionAttributes;
  expiresAt?: string;
  name?: string;
};

/**
 * Every field the mutation accepts. A misspelling is a compile error — `take` checks the keys
 * against `ApiKeyInput` — but an omission is not, so the round-trip test in `ApiKeyInput.spec.ts`
 * is what catches a field this list forgets.
 */
const GQL_FIELDS = ['accountId', 'permission', 'expiresAt', 'name'] as const;

/** `take()` drops undefined keys, so an unset name or expiry never reaches the wire as null. */
export const toGqlInput = (input: ApiKeyInput): GQLInput =>
  take<GQLInput, ApiKeyInput>(input, ...GQL_FIELDS);
